<template>
  <div class="page-container" :class="{ 'page-container--with-sidebar': isSidebarPresent }">
    <div class="finalized-documents__header">
      <div class="finalized-documents__header-row">
        <h1 class="page-title">Hramba</h1>
        <search class="finalized-documents__search" has-search-icon input-label="Išči" @change="setQuery" />
        <v-button class="finalized-documents__add-button" @click="openCreateFinalizedDocument">
          <icon name="plus" :width="20" :height="20" /> Nov dokument
        </v-button>
        <v-button class="finalized-documents__add-button" @click="openCreateEnvelope">
          <icon name="plus" :width="20" :height="20" /> Nova kuverta
        </v-button>
      </div>

      <folder-tiles
        :folders="folders"
        :selected-folder-id="selectedFolderId"
        @select="onFolderSelect"
        @layout-change="refreshStickyTableHeader"
      />

      <filters
        :filters="filters"
        :available-filters="availableFilters"
        @change="onFilterChange"
      />

      <actions
        class="finalized-documents__actions"
        :actions-state="actionsState"
        @downloadDocx="downloadFinalizedDocumentDocx"
        @downloadPdf="downloadFinalizedDocumentPdf"
        @edit="openEditFinalizedDocument"
        @sign="eSignDocument"
        @remove="removeFinalizedDocument"
      />
    </div>

    <sticky-table-header ref="stickyTableHeader" />

    <div class="admin__content" v-if="numberOfPages">
      <entity-table
        class="finalized-documents__table"
        ref="table"
        :columns="finalizedDocumentColumns"
        :sort-by="transformedSortBy"
        :items="envelopes"
        :are-items-clickable="false"
        :is-search-present="false"
        :is-add-new-present="false"
        :is-select-all-present="true"
        :is-loaded="!!numberOfPages"
        :empty-text="emptyText"
        :pagination="pagination"
        @select-all="selectAllFinalizedDocuments"
        @column-click="setSortBy($event)"
        @page-change="goToPage"
      >
        <finalized-documents-loading v-if="isLoadingEnvelopes" />
        <template v-for="(envelope, index) in augmentedEnvelopes" v-else>
          <envelope
            :key="`envelope-${envelope.id}`"
            :envelope="envelope"
            :custom-attribute-columns="customAttributeColumns"
            :text-finalized-document-columns="textFinalizedDocumentColumns"
            :expanded-envelope-ids="expandedEnvelopeIds"
            @toggle-envelope="toggleEnvelope"
            @filter-by-envelope="filterByEnvelope"
            :class="{ 'envelope__row--even': index % 2 === 0 }"
          />
          <template v-for="finalizedDocument in envelope.finalizedDocuments">
            <finalized-document
              :key="`finalized-document-${finalizedDocument.id}`"
              v-show="expandedEnvelopeIds.includes(envelope.id) && !finalizedDocument.isHidden"
              :finalized-document="finalizedDocument"
              :custom-attribute-columns="customAttributeColumns"
              :text-finalized-document-columns="textFinalizedDocumentColumns"
            />
          </template>
        </template>
      </entity-table>
    </div>
  </div>
</template>

<script>
import { toCamelCase } from 'js-convert-case'
import { mapState, mapGetters, mapActions } from 'vuex'
import EntityTable from '@/components/shared/EntityTable'
import Filters from '@/components/shared/Filters'
import Envelope from './Envelope'
import Actions from './Actions'
import FinalizedDocument from './FinalizedDocument'
import FinalizedDocumentsLoading from './FinalizedDocumentsLoading'
import FolderTiles from './FolderTiles'
import StickyTableHeader from './StickyTableHeader'
import VButton from 'design-system/VButton.vue'
import Icon from 'design-system/icons/Icon.vue'
import PaginationMixin from '../../../shared/PaginationMixin'
import Search from '@/components/shared/_Search'
import prompt from 'design-system/components/prompt/prompt'

export default {
  props: {
    wasDocumentJustESigned: { type: Boolean, default: false },
    onlyRecentDocuments: { type: Boolean, default: false },
    finalizedDocumentId: { type: String, required: false },
  },
  data () {
    return {
      expandedEnvelopeIds: [],
    }
  },
  components: {
    Actions,
    Envelope,
    FinalizedDocument,
    Filters,
    EntityTable,
    FinalizedDocumentsLoading,
    FolderTiles,
    StickyTableHeader,
    VButton,
    Icon,
    Search,
  },
  mixins: [PaginationMixin],
  beforeCreate () {
    this.textFinalizedDocumentColumns = [
      { id: 'name', name: 'Dokument', sortById: 'name' },
      { id: 'truncatedDescription', name: 'Opis' },
      { id: 'counterpartyName', name: 'Stranka', sortById: 'counterpartyName' },
      { id: 'status', name: 'Status' },
      { id: 'formattedEffectiveOn', name: 'Začetek učinkovanja', sortById: 'effectiveOn' },
      { id: 'formattedExpiresOn', name: 'Prenehanje', sortById: 'expiresOn' },
    ]
  },
  created () {
    this.storeModule = ['accountStore', 'finalizedDocuments']

    this.initializeStorage().then(async () => {
      if (this.finalizedDocumentId) {
        await this.filterById(this.finalizedDocumentId)
      }
      this.$nextTick(() => this.refreshStickyTableHeader())
    })
  },
  mounted () {
    this.$nextTick(() => this.refreshStickyTableHeader())

    if (this.wasDocumentJustESigned) {
      this.setMessage({
        type: 'info',
        text: 'Dokument je bil uspešno podpisan.',
      })
    }
  },
  beforeDestroy () {
    this.reset()
  },
  watch: {
    $route () {
      this.initializeStorage().then(() => {
        this.$nextTick(() => this.refreshStickyTableHeader())
      })
    },
    envelopes: {
      handler (newEnvelopes) {
        if (newEnvelopes.length === 1 && newEnvelopes[0].finalizedDocuments.length > 1) {
          this.expandedEnvelopeIds = [newEnvelopes[0].id]
        } else {
          this.expandedEnvelopeIds = []
        }
        this.$nextTick(() => this.refreshStickyTableHeader())
      },
      deep: true,
    },
    sortBy () {
      this.$nextTick(() => this.refreshStickyTableHeader())
    },
    selectedFinalizedDocuments () {
      this.$nextTick(() => this.$refs.stickyTableHeader?.syncSelectAllState())
    },
  },
  computed: {
    ...mapState('accountStore/finalizedDocuments', [
      'numberOfPages',
      'folders',
      'selectedFolderId',
      'availableCustomAttributeKeys',
      'sortBy',
      'filters',
      'query',
      'isLoadingEnvelopes',
      'selectedFinalizedDocuments',
    ]),
    ...mapGetters(['accountSlug', 'getAccountIdForPath']),
    ...mapGetters('accountStore/finalizedDocuments', ['envelopes', 'availableFilters', 'idFilter', 'isFilteringByEnvelope']),
    augmentedEnvelopes () {
      return this.envelopes.map(envelope => {
        envelope.finalizedDocuments.forEach(finalizedDocument => {
          const isDescriptionLong = finalizedDocument.description && finalizedDocument.description.length > 80
          finalizedDocument.truncatedDescription = isDescriptionLong ? finalizedDocument.description.slice(0, 65) + '...' : finalizedDocument.description
          finalizedDocument.isDescriptionTruncated = isDescriptionLong
        })
        const firstFinalizedDocumentData = envelope.finalizedDocuments.length > 0 ? envelope.finalizedDocuments[0] : {}
        const isExpanded = this.expandedEnvelopeIds.includes(envelope.id)

        return {
          ...firstFinalizedDocumentData,
          ...envelope,
          isExpanded: isExpanded,
          icon: isExpanded ? 'envelope-open' : 'envelope',
        }
      })
    },
    customAttributeColumns () {
      return this.availableCustomAttributeKeys.map(customAttributeKey => {
        return {
          id: customAttributeKey.id,
          name: customAttributeKey.label,
        }
      })
    },
    finalizedDocumentColumns () {
      return this.textFinalizedDocumentColumns.concat(this.customAttributeColumns)
    },
    transformedSortBy () {
      if (!this.sortBy || !this.sortBy.replace('-', '').startsWith('finalized_documents')) {
        return null
      }

      const splitSortBy = this.sortBy.split('.')
      const transformedSortBy = toCamelCase(splitSortBy[splitSortBy.length - 1])
      return this.sortBy.startsWith('-') ? `-${transformedSortBy}` : transformedSortBy
    },
    selectedFinalizedDocument () {
      if (this.selectedFinalizedDocuments.length !== 1) {
        return null
      }
      return this.selectedFinalizedDocuments[0]
    },
    actionsState () {
      let editTooltip, downloadTooltip, deleteTooltip = null
      const documentIsNotPurchased = this.selectedFinalizedDocument && !this.selectedFinalizedDocument.isPurchased

      if (documentIsNotPurchased) {
        downloadTooltip = 'Pred prenosom je dokument potrebno plačati.'
        editTooltip = 'Pred urejanjem je dokument potrebno plačati.'
      }

      if (this.selectedFinalizedDocuments.length < 1) {
        editTooltip = 'Izberi vsaj en dokument za urejanje.'
        downloadTooltip = 'Izberi dokument za prenos.'
        deleteTooltip = 'Izberi dokument za brisanje.'
      }

      if (this.selectedFinalizedDocuments.length > 1) {
        downloadTooltip = 'Prenos je mogoč le za en dokument naenkrat.'
        deleteTooltip = 'Brisanje je mogoče le za en dokument naenkrat.'
      }

      return {
        edit: {
          label: this.selectedFinalizedDocuments.length > 1 ? `Uredi (${this.selectedFinalizedDocuments.length})` : 'Uredi',
          disabled: this.selectedFinalizedDocuments.length < 1 || documentIsNotPurchased,
          tooltip: editTooltip,
        },
        download: {
          label: 'Prenesi',
          disabled: !this.selectedFinalizedDocument || documentIsNotPurchased,
          tooltip: downloadTooltip,
        },
        downloadPdf: {
          label: 'Prenesi PDF',
          disabled: !this.selectedFinalizedDocument || documentIsNotPurchased || !this.selectedFinalizedDocument.pdfDownloadUrl,
        },
        downloadDocx: {
          label: 'Prenesi DOCX',
          disabled: !this.selectedFinalizedDocument || documentIsNotPurchased || !this.selectedFinalizedDocument.docxDownloadUrl,
        },
        delete: {
          label: 'Izbriši',
          disabled: !this.selectedFinalizedDocument,
          tooltip: deleteTooltip,
        },
      }
    },
    isDocumentSignable () {
      if (!this.selectedFinalizedDocument) {
        return false
      }

      return this.selectedFinalizedDocument.isPurchased && !this.selectedFinalizedDocument.isESigned && !this.selectedFinalizedDocument.signedOn
    },
    emptyText () {
      if (this.envelopes.length > 0) {
        return null
      }

      const areFiltersPresent = this.filters.filter(f => f.valueId && f.valueId !== '').length > 0 || !!this.query
      return areFiltersPresent ? 'Noben dokument ne ustreza iskanju.' : 'V hrambi ni nobenega dokumenta.'
    },
  },
  methods: {
    ...mapActions('accountStore/finalizedDocuments', [
      'initialize',
      'reset',
      'setQuery',
      'setFilters',
      'filterById',
      'setSortBy',
      'goToPage',
      'deleteFinalizedDocument',
      'selectAllFinalizedDocuments',
    ]),
    ...mapActions(['setMessage']),
    onFilterChange (filters) {
      const isFilteringByStatus = filters.filter(f => f.id === 'by_finalized_document_status')
      if (isFilteringByStatus) {
        this.expandedEnvelopeIds = this.envelopes
          .filter(envelope => envelope.finalizedDocuments.length > 1)
          .map(envelope => envelope.id)
      }

      this.setFilters(filters)
    },
    initializeStorage () {
      return this.initialize({
        onlyRecentDocuments: this.onlyRecentDocuments,
        folderId: this.$route.query.folder_id,
        finalizedDocumentId: this.finalizedDocumentId,
      })
    },
    onFolderSelect (folder) {
      const folderId = folder ? folder.id : null
      if (folderId === this.selectedFolderId) {
        return
      }

      const query = { ...this.$route.query }
      delete query.id
      if (folderId == null) {
        delete query.folder_id
      } else {
        query.folder_id = folderId.toString()
      }

      this.$router.replace({ query }).catch(() => {})
    },
    filterByEnvelope (envelopeId) {
      this.setFilters([
        {
          id: 'by_envelope',
          valueId: envelopeId,
          valueLabel: envelopeId,
        },
      ])
    },
    toggleEnvelope (envelopeId) {
      if (this.expandedEnvelopeIds.includes(envelopeId)) {
        this.expandedEnvelopeIds = this.expandedEnvelopeIds.filter(id => id !== envelopeId)
      } else {
        this.expandedEnvelopeIds.push(envelopeId)
      }
    },
    openCreateFinalizedDocument () {
      this.$router.push({
        name: 'account_finalized_document_create_path',
        params: {
          accountId: this.accountSlug,
        },
      })
    },
    openCreateEnvelope () {
      this.$router.push({
        name: 'account_envelope_create_path',
        params: {
          accountId: this.accountSlug,
        },
      })
    },
    downloadFinalizedDocumentDocx () {
      if (!this.selectedFinalizedDocument || !this.selectedFinalizedDocument.docxDownloadUrl) {
        return
      }

      window.open(this.selectedFinalizedDocument.docxDownloadUrl, '_blank')
    },
    downloadFinalizedDocumentPdf () {
      if (!this.selectedFinalizedDocument || !this.selectedFinalizedDocument.pdfDownloadUrl) {
        return
      }

      window.open(this.selectedFinalizedDocument.pdfDownloadUrl, '_blank')
    },
    openEditFinalizedDocument () {
      if (this.selectedFinalizedDocuments.length < 1) {
        return
      }

      const id = this.selectedFinalizedDocument ? this.selectedFinalizedDocument.id : this.selectedFinalizedDocuments.map(d => d.id).join(',')
      this.$router.push({
        name: 'account_finalized_document_edit_path',
        params: {
          id,
          accountId: this.accountSlug,
        },
      })
    },
    async removeFinalizedDocument () {
      if (!this.selectedFinalizedDocument) {
        return
      }

      const result = await prompt.okText('Izbriši').confirm('Res želite izbrisati ta dokument? Akcija je nepovratna.')
      if (result) {
        this.deleteFinalizedDocument(this.selectedFinalizedDocument.id)
      }
    },
    eSignDocument () {
      if (!this.selectedFinalizedDocument || this.selectedFinalizedDocument.isESigned) {
        return
      }

      this.$router.push({
        name: 'account_finalized_document_e_signature_path',
        params: {
          id: this.selectedFinalizedDocument.id,
          accountId: this.getAccountIdForPath(this.selectedFinalizedDocument.accountId),
        },
      })
    },
    refreshStickyTableHeader () {
      const pageHeader = this.$el.querySelector('.finalized-documents__header')
      const tableHeaderElement = this.$refs.table?.getTableHeaderElement()
      this.$refs.stickyTableHeader?.refresh(pageHeader, tableHeaderElement)
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'app/assets/stylesheets/variables';
@import '@/components/common';
@import '@/components/admin_common';

.admin__content {
  position: relative;

  @include respond-to(small) {
    margin-top: 16px;
  }
}

.finalized-documents__header {
  position: sticky;
  top: 64px;
  z-index: $z_pop;
  background-color: $background_color;

  @include respond-to(small) {
    position: inherit;
    top: 0;
  }
}

.finalized-documents__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  & .page-title {
    margin: 0;
    white-space: nowrap;
  }

  & .finalized-documents__search {
    margin-right: 16px;
    margin-left: 24px;
    min-width: 120px;

    ::v-deep  .md-input {
      width: 120px;
    }
  }

  @include respond-to(small) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;

    & .finalized-documents__search {
      margin-right: 0;
      margin-left: 0;
    }
  }
}

.finalized-documents__add-button {
  flex-shrink: 0;

  .icon-wrapper {
    position: relative;
    top: 2px;
    margin-right: 12px;
  }

  &:last-child {
    margin-left: 8px;
  }

  @include respond-to(small) {
    &:last-child {
      margin-top: 8px;
      margin-left: 0;
    }
  }
}

.finalized-documents__table {
  position: relative;
}
</style>
